<template>
    <section class="content">
      <div class="container-fluid">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">LOGS ONLINE PAYMENT</h3>
          </div>
  
          <div class="card-body">
  
            <!-- FILTER -->
            <div class="row mb-3">
              <div class="col-md-3">
                <KantorCabang
                  v-model="filter.kantor"
                  label="Cabang"
                  @input="onFilterChange"
                />
              </div>
  
              <div class="col-md-3">
                <v-select
                  v-model="filter.status"
                  label="Status"
                  dense
                  clearable
                  :items="statusItems"
                  item-text="text"
                  item-value="value"
                  @change="onFilterChange"
                />
              </div>
  
              <div class="col-md-4">
                <TextSearch
                  v-model="filter.search"
                  label="Cari Nama / Invoice / Kode Midtrans"
                  @input="onFilterChange"
                />
              </div>
  
              <!-- FILTER TANGGAL -->
              <div class="col-md-4 mt-2">
                <v-menu
                  v-model="menuTanggal"
                  :close-on-content-click="false"
                  offset-y
                  max-width="290"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="tanggalLabel"
                      label="Tanggal Pembayaran"
                      dense
                      readonly
                      clearable
                      hint="Pilih tanggal awal dan akhir"
                      persistent-hint
                      v-bind="attrs"
                      v-on="on"
                      @click:clear="clearTanggal"
                    />
                  </template>
  
                  <v-date-picker
                    v-model="filter.tanggal"
                    range
                    locale="id-ID"
                    @change="onTanggalChange"
                  />
                </v-menu>
              </div>
            </div>
  
            <!-- TABLE -->
            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loading"
              :items-per-page="pagination.itemsPerPage"
              :page.sync="pagination.page"
              :server-items-length="totalItems"
              @update:page="fetchData"
              @update:items-per-page="changeItemsPerPage"
            >
              <template #item.no="{ index }">
                {{ (pagination.page - 1) * pagination.itemsPerPage + index + 1 }}
              </template>
  
              <!-- PELANGGAN -->
              <template #item.nomor_pelanggan="{ item }">
                <div class="cell-block">
                  <div class="cell-title">{{ item.nama_pelanggan }}</div>
                  <div class="cell-sub">ID: {{ item.nomor_pelanggan }}</div>
                  <div class="cell-sub muted">{{ item.cabang }}</div>
                </div>
              </template>
  
              <!-- INVOICE -->
              <template #item.no_invoice="{ item }">
                <div class="cell-block">
                  <div class="cell-title">{{ item.no_invoice }}</div>
                  <div class="cell-sub">Bulan {{ item.bulan_pemakaian }}</div>
                  <div class="cell-amount">{{ formatRupiah(item.tagihan) }}</div>
                </div>
              </template>
  
              <template #item.amount="{ item }">
                {{ formatRupiah(item.amount) }}
              </template>
  
              <template #item.fee="{ item }">
                {{ formatRupiah(item.fee) }}
              </template>
  
              <!-- STATUS + CHANNEL -->
              <template #item.status="{ item }">
                <div class="status-block">
                  <v-chip small dark :color="statusColor(item.status)" class="mb-1">
                    {{ item.status.toUpperCase() }}
                  </v-chip>
                  <v-chip small outlined color="primary">
                    {{ item.kodeChannel }}
                  </v-chip>
                </div>
              </template>
  
              <!-- PAID AT / TANGGAL -->
              <template #item.paid_at="{ item }">
                <div class="paid-at">
                  <!-- SETTLEMENT -->
                  <template v-if="item.status === 'settlement' && item.paid_at">
                    <div class="paid-date text-success">
                      {{ formatTanggalIndo(item.paid_at).tanggal }}
                    </div>
                    <div class="paid-time">
                      {{ formatTanggalIndo(item.paid_at).jam }}
                    </div>
                    <div class="paid-label text-success">(Dibayar)</div>
                  </template>
                  
                  <!-- PENDING -->
                  <template v-else-if="item.status === 'pending'">
                    <div class="paid-date text-warning">
                      {{ formatTanggalIndo(item.created_at).tanggal }}
                    </div>
                    <div class="paid-time">
                      {{ formatTanggalIndo(item.created_at).jam }}
                    </div>
                    <div class="paid-label text-warning">(Dibuat)</div>
                  </template>

                  <!-- EXPIRE -->
                  <template v-else-if="item.status === 'expire'">
                    <template v-if="getExpiredDate(item)">
                      <div class="paid-date text-danger">
                        {{ formatTanggalIndo(getExpiredDate(item)).tanggal }}
                      </div>
                      <div class="paid-time">
                        {{ formatTanggalIndo(getExpiredDate(item)).jam }}
                      </div>
                      <div class="paid-label text-danger">(Expired)</div>
                    </template>
                    <template v-else>
                      <div class="paid-date text-danger">-</div>
                      <div class="paid-label text-danger">(Expired)</div>
                    </template>
                  </template>

                  <!-- FALLBACK -->
                  <template v-else>
                    <div class="paid-date">
                      {{ formatTanggalIndo(item.paid_at || item.created_at).tanggal }}
                    </div>
                    <div class="paid-time">
                      {{ formatTanggalIndo(item.paid_at || item.created_at).jam }}
                    </div>
                  </template>
                </div>
              </template>

              <!-- AKSI / DETAIL -->
              <template #item.aksi="{ item }">
                <v-btn
                  x-small
                  color="primary"
                  outlined
                  @click="openDetail(item)"
                >
                  <v-icon x-small left>mdi-eye</v-icon>
                  Detail
                </v-btn>
              </template>
            </v-data-table>
  
          </div>
        </div>
      </div>

      <!-- MODAL DETAIL TRANSAKSI -->
      <v-dialog v-model="dialogDetail" max-width="700px">
        <v-card v-if="selectedItem">
          <v-card-title class="headline primary white--text py-3">
            <v-icon left dark>mdi-file-document-outline</v-icon>
            Detail Transaksi Midtrans
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogDetail = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-row dense>
              <!-- STATUS & CHANNEL -->
              <v-col cols="12" class="d-flex justify-space-between align-center mb-2">
                <span class="grey--text font-weight-bold">Status Transaksi</span>
                <div>
                  <v-chip small dark :color="statusColor(selectedItem.status)" class="mr-1">
                    {{ selectedItem.status.toUpperCase() }}
                  </v-chip>
                  <v-chip small outlined color="primary">
                    {{ selectedItem.kodeChannel }}
                  </v-chip>
                </div>
              </v-col>

              <v-col cols="12"><v-divider class="my-1"></v-divider></v-col>

              <!-- KODE & INVOICE -->
              <v-col cols="6">
                <div class="caption grey--text">Kode Midtrans</div>
                <div class="subtitle-2 font-weight-bold">{{ selectedItem.id }}</div>
              </v-col>

              <v-col cols="6">
                <div class="caption grey--text">No. Invoice</div>
                <div class="subtitle-2 font-weight-bold">{{ selectedItem.no_invoice }} (Bulan {{ selectedItem.bulan_pemakaian }})</div>
              </v-col>

              <!-- PELANGGAN -->
              <v-col cols="6" class="mt-2">
                <div class="caption grey--text">ID Pelanggan</div>
                <div class="body-2 font-weight-medium">{{ selectedItem.nomor_pelanggan }}</div>
              </v-col>

              <v-col cols="6" class="mt-2">
                <div class="caption grey--text">Nama Pelanggan</div>
                <div class="body-2 font-weight-medium">{{ selectedItem.nama_pelanggan }}</div>
              </v-col>

              <v-col cols="12" class="mt-2">
                <div class="caption grey--text">Kantor Cabang</div>
                <div class="body-2">{{ selectedItem.cabang }}</div>
              </v-col>

              <v-col cols="12"><v-divider class="my-2"></v-divider></v-col>

              <!-- NOMINAL -->
              <v-col cols="4">
                <div class="caption grey--text">Tagihan Invoice</div>
                <div class="body-2 font-weight-bold">{{ formatRupiah(selectedItem.tagihan) }}</div>
              </v-col>

              <v-col cols="4">
                <div class="caption grey--text">Bayar Midtrans</div>
                <div class="body-2 font-weight-bold text-primary">{{ formatRupiah(selectedItem.amount) }}</div>
              </v-col>

              <v-col cols="4">
                <div class="caption grey--text">Fee / Admin</div>
                <div class="body-2 font-weight-bold">{{ formatRupiah(selectedItem.fee) }}</div>
              </v-col>

              <v-col cols="12"><v-divider class="my-2"></v-divider></v-col>

              <!-- RIWAYAT TANGGAL -->
              <v-col cols="12">
                <div class="subtitle-2 font-weight-bold mb-1">Riwayat Tanggal</div>
              </v-col>

              <v-col cols="6">
                <div class="caption grey--text">Tanggal Dibuat (Pending)</div>
                <div class="body-2 font-weight-medium">
                  {{ selectedItem.created_at ? formatTanggalIndo(selectedItem.created_at).tanggal + ' ' + formatTanggalIndo(selectedItem.created_at).jam : '-' }}
                </div>
              </v-col>

              <v-col cols="6">
                <div class="caption grey--text">Tanggal Dibayar (Settlement)</div>
                <div class="body-2 font-weight-medium" :class="selectedItem.paid_at ? 'green--text font-weight-bold' : 'orange--text'">
                  {{ selectedItem.paid_at ? formatTanggalIndo(selectedItem.paid_at).tanggal + ' ' + formatTanggalIndo(selectedItem.paid_at).jam : 'Belum Dibayar' }}
                </div>
              </v-col>

              <v-col cols="6" class="mt-2" v-if="selectedItem.status === 'expire' || getExpiredDate(selectedItem)">
                <div class="caption grey--text">Tanggal Expired / Perubahan</div>
                <div class="body-2 font-weight-medium text-danger">
                  {{ getExpiredDate(selectedItem) ? formatTanggalIndo(getExpiredDate(selectedItem)).tanggal + ' ' + formatTanggalIndo(getExpiredDate(selectedItem)).jam : '-' }}
                </div>
              </v-col>

              <!-- MIDTRANS PAYMENT LOGS (midtrans_payment_logs) -->
              <v-col cols="12"><v-divider class="my-2"></v-divider></v-col>

              <v-col cols="12">
                <div class="subtitle-2 font-weight-bold mb-2">
                  <v-icon small class="mr-1">mdi-history</v-icon>
                  Log Callback / Riwayat (midtrans_payment_logs)
                </div>

                <div v-if="!selectedItem.logs || selectedItem.logs.length === 0" class="caption grey--text font-italic p-2 text-center border rounded">
                  Belum ada log callback tercatat pada midtrans_payment_logs.
                </div>

                <v-simple-table v-else dense class="elevation-1 border">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left" style="font-size: 11px;">#</th>
                        <th class="text-left" style="font-size: 11px;">Status Log</th>
                        <th class="text-left" style="font-size: 11px;">Waktu Log</th>
                        <th class="text-left" style="font-size: 11px;">Detail Log</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(log, idx) in selectedItem.logs">
                        <tr :key="'row-' + (log.id || idx)">
                          <td style="font-size: 11px;">{{ idx + 1 }}</td>
                          <td style="font-size: 11px;">
                            <v-chip x-small dark :color="statusColor(log.status)">
                              {{ (log.status || '-').toUpperCase() }}
                            </v-chip>
                          </td>
                          <td style="font-size: 11px;">
                            {{ log.created_at ? formatTanggalIndo(log.created_at).tanggal + ' ' + formatTanggalIndo(log.created_at).jam : (log.updated_at ? formatTanggalIndo(log.updated_at).tanggal + ' ' + formatTanggalIndo(log.updated_at).jam : '-') }}
                          </td>
                          <td style="font-size: 11px;" class="py-1">
                            <div v-if="log.message || log.transaction_status" class="caption font-weight-medium mb-1">
                              {{ log.message || log.transaction_status }}
                            </div>
                            <v-btn
                              x-small
                              color="info"
                              outlined
                              @click="toggleLogExpand(idx)"
                            >
                              <v-icon x-small left>{{ expandedLogIdx === idx ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                              {{ expandedLogIdx === idx ? 'Tutup Detail' : 'Buka Raw Log' }}
                            </v-btn>
                            <v-btn
                              x-small
                              color="primary"
                              text
                              class="ml-1"
                              @click="showRawLog(log)"
                            >
                              <v-icon x-small left>mdi-open-in-new</v-icon>
                              Pop-up
                            </v-btn>
                          </td>
                        </tr>
                        <!-- EXPANDED INLINE ROW -->
                        <tr v-if="expandedLogIdx === idx" :key="'expand-' + (log.id || idx)" class="grey lighten-4">
                          <td colspan="4" class="p-2">
                            <pre style="background: #1e1e1e; color: #00ff66; padding: 10px; border-radius: 4px; font-size: 11px; max-height: 250px; overflow: auto; margin: 4px 0; white-space: pre-wrap; word-break: break-all;">{{ formatLogJson(log) }}</pre>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="secondary" text @click="dialogDetail = false">
              Tutup
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- DIALOG RAW LOG JSON -->
      <v-dialog v-model="dialogRawLog" max-width="650px" attach="body">
        <v-card>
          <v-card-title class="headline primary white--text py-2 text-body-1 font-weight-bold">
            <v-icon left dark small>mdi-code-json</v-icon>
            Detail Raw Log Record
            <v-spacer></v-spacer>
            <v-btn icon dark small @click="dialogRawLog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-3">
            <pre style="background: #1e1e1e; color: #00ff66; padding: 12px; border-radius: 4px; font-size: 11px; max-height: 450px; overflow: auto; white-space: pre-wrap; word-break: break-all;">{{ rawLogContent }}</pre>
          </v-card-text>
          <v-card-actions class="px-4 pb-3">
            <v-spacer></v-spacer>
            <v-btn small color="secondary" text @click="dialogRawLog = false">Tutup</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </section>
  </template>

  <script>
import KantorCabang from '../../base-components/KantorCabang.vue'
import TextSearch from '../../base-components/TextSearch.vue'
import { formatRupiah } from '../../helpers/format'

const getTodayFormatted = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  components: { KantorCabang, TextSearch },

  data() {
    const today = getTodayFormatted()

    return {
      loading: false,
      items: [],
      totalItems: 0,
      menuTanggal: false,
      dialogDetail: false,
      selectedItem: null,
      dialogRawLog: false,
      rawLogContent: '',
      expandedLogIdx: null,

      pagination: {
        page: 1,
        itemsPerPage: 50
      },

      filter: {
        kantor: null,
        status: 'settlement',
        search: null,
        tanggal: [today, today]
      },

      statusItems: [
        { text: 'Semua Status', value: 'all' },
        { text: 'Settlement', value: 'settlement' },
        { text: 'Pending', value: 'pending' },
        { text: 'Expire', value: 'expire' }
      ],

      headers: [
        { text: 'No', value: 'no' },
        { text: 'Kode Midtrans', value: 'id' },
        { text: 'Pelanggan', value: 'nomor_pelanggan' },
        { text: 'Invoice', value: 'no_invoice' },
        { text: 'Bayar', value: 'amount', align: 'right' },
        { text: 'Fee', value: 'fee', align: 'right' },
        { text: 'Status', value: 'status' },
        { text: 'Tanggal', value: 'paid_at' },
        { text: 'Aksi', value: 'aksi', sortable: false, align: 'center' }
      ]
    }
  },

  computed: {
    tanggalLabel() {
      if (!this.filter.tanggal || this.filter.tanggal.length === 0) {
        return ''
      }
      if (
        this.filter.tanggal.length === 1 ||
        this.filter.tanggal[0] === this.filter.tanggal[1]
      ) {
        return this.formatDateShort(this.filter.tanggal[0])
      }
      if (this.filter.tanggal.length === 2) {
        const [start, end] = this.filter.tanggal
        return `${this.formatDateShort(start)} - ${this.formatDateShort(end)}`
      }
      return ''
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    formatRupiah,

    formatDateShort(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },

    formatTanggalIndo(datetime) {
      if (!datetime) return { tanggal: '-', jam: '-' }

      const [d, t] = datetime.split(' ')
      const [y, m, day] = d.split('-')
      const [hh, mm] = t.split(':')

      const date = new Date(y, m - 1, day, hh, mm)

      return {
        tanggal: date.toLocaleDateString('id-ID', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        jam: date.toLocaleTimeString('id-ID', {
          hour: '2-digit',
          minute: '2-digit'
        }) + ' WIB'
      }
    },

    statusColor(status) {
      if (status === 'settlement') return 'green'
      if (status === 'pending') return 'orange'
      if (status === 'expire') return 'red'
      return 'grey'
    },

    getExpiredDate(item) {
      if (!item) return null

      // Loop seluruh logs di item.logs untuk mencari expiry_time dari Midtrans
      if (item.logs && Array.isArray(item.logs) && item.logs.length > 0) {
        const checkPayload = (payload) => {
          if (!payload) return null
          try {
            const obj = typeof payload === 'string' ? JSON.parse(payload) : payload
            if (obj && obj.expiry_time) return obj.expiry_time
          } catch (e) {}
          return null
        }

        // 1. Prioritas utama: Ekstrak expiry_time dari response_payload / raw_response / request_payload
        for (const l of item.logs) {
          if (l.expiry_time) return l.expiry_time

          const expiry = checkPayload(l.response_payload) || 
                         checkPayload(l.raw_response) || 
                         checkPayload(l.request_payload)
          if (expiry) return expiry
        }

        // 2. Cari log dengan status 'expire' / 'expired'
        const expireLog = item.logs.find(l => 
          l.status === 'expire' || 
          l.status === 'expired' || 
          l.transaction_status === 'expire' ||
          l.transaction_status === 'expired'
        )

        if (expireLog) {
          if (expireLog.created_at) return expireLog.created_at
          if (expireLog.updated_at) return expireLog.updated_at
        }

        // 3. Jika status transaksi expire, ambil log paling terakhir yang waktunya beda dari created_at
        if (item.status === 'expire' || item.status === 'expired') {
          const lastLog = item.logs[item.logs.length - 1]
          if (lastLog && (lastLog.created_at || lastLog.updated_at)) {
            const logTime = lastLog.created_at || lastLog.updated_at
            if (logTime !== item.created_at) {
              return logTime
            }
          }
        }
      }

      // 4. Cek m.updated_at jika tidak sama dengan m.created_at
      if (item.updated_at && item.updated_at !== item.created_at) {
        return item.updated_at
      }

      return null
    },

    openDetail(item) {
      this.selectedItem = item
      this.expandedLogIdx = null
      this.dialogDetail = true
    },

    toggleLogExpand(idx) {
      this.expandedLogIdx = this.expandedLogIdx === idx ? null : idx
    },

    showRawLog(log) {
      this.rawLogContent = this.formatLogJson(log)
      this.dialogRawLog = true
    },

    formatLogJson(log) {
      try {
        const copy = { ...log }
        Object.keys(copy).forEach(key => {
          if (typeof copy[key] === 'string' && (copy[key].startsWith('{') || copy[key].startsWith('['))) {
            try {
              copy[key] = JSON.parse(copy[key])
            } catch (e) {}
          }
        })
        return JSON.stringify(copy, null, 2)
      } catch (e) {
        return JSON.stringify(log, null, 2)
      }
    },

    onTanggalChange(val) {
      if (val.length === 2) {
        this.menuTanggal = false
        this.onFilterChange()
      } else if (val.length === 1) {
        this.onFilterChange()
      }
    },

    clearTanggal() {
      this.filter.tanggal = []
      this.onFilterChange()
    },

    onFilterChange() {
      this.pagination.page = 1
      this.fetchData()
    },

    async fetchData() {
      this.loading = true
      try {
        const dateFrom =
          this.filter.tanggal && this.filter.tanggal.length > 0
            ? this.filter.tanggal[0]
            : null
        const dateTo =
          this.filter.tanggal && this.filter.tanggal.length > 1
            ? this.filter.tanggal[1]
            : dateFrom

        const res = await axios.get('/api/logs/payment', {
          params: {
            page: this.pagination.page,
            per_page: this.pagination.itemsPerPage,
            kantor_id: this.filter.kantor?.id || null,
            status: this.filter.status,
            search: this.filter.search,
            paid_from: dateFrom,
            paid_to: dateTo
          }
        })

        this.items = res.data.data.data
        this.totalItems = res.data.data.total
      } finally {
        this.loading = false
      }
    },
  
    changeItemsPerPage(val) {
      this.pagination.itemsPerPage = val
      this.pagination.page = 1
      this.fetchData()
    }
  }
}
</script>
  
<style scoped>
.status-block {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.paid-at {
  line-height: 1.3;
}

.paid-date {
  font-weight: 600;
  font-size: 12px;
}

.paid-time {
  font-size: 11px;
  color: #666;
}

.paid-label {
  font-size: 10px;
  font-weight: 500;
}
</style>